import { useEffect, useMemo, useRef, useState } from 'react';
import Fuse from 'fuse.js';
import { IconSearch } from '@/lib/icons';
import type { HelpSearchEntry } from '@/content/help/types';

interface Props {
  value: string;
  onChange: (next: string) => void;
  onResults: (results: HelpSearchEntry[]) => void;
  placeholder?: string;
  autoFocus?: boolean;
  className?: string;
  maxResults?: number;
}

const DEBOUNCE_MS = 150;

let searchIndexPromise: Promise<HelpSearchEntry[]> | null = null;

function loadSearchIndex(): Promise<HelpSearchEntry[]> {
  if (!searchIndexPromise) {
    searchIndexPromise = import('@/content/help/_searchIndex.json').then(
      (m) => (m.default as HelpSearchEntry[]) ?? [],
    );
  }
  return searchIndexPromise;
}

// HelpSearchInput is a debounced text input that lazily loads the help
// search index on first focus. The parent owns the result rendering — this
// component just emits result lists through onResults.
export function HelpSearchInput({
  value,
  onChange,
  onResults,
  placeholder,
  autoFocus,
  className,
  maxResults = 10,
}: Props): JSX.Element {
  const [index, setIndex] = useState<HelpSearchEntry[] | null>(null);
  const [hasFocused, setHasFocused] = useState(autoFocus === true);
  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    if (!hasFocused) return;
    let cancelled = false;
    loadSearchIndex().then((entries) => {
      if (!cancelled) setIndex(entries);
    });
    return () => {
      cancelled = true;
    };
  }, [hasFocused]);

  const fuse = useMemo(() => {
    if (!index) return null;
    return new Fuse<HelpSearchEntry>(index, {
      keys: [
        { name: 'title', weight: 0.4 },
        { name: 'summary', weight: 0.3 },
        { name: 'tags', weight: 0.2 },
        { name: 'body', weight: 0.1 },
      ],
      threshold: 0.3,
      includeMatches: true,
      // K2.7 retune: keep the documented weights and threshold, but match
      // anywhere in a field (ignoreLocation) and treat a multi-word query as
      // an AND of fuzzy terms (useExtendedSearch). Without these, two-word
      // queries like "cancel pipeline" and "delete article" returned nothing
      // because Fuse matched the full string contiguously near the start.
      ignoreLocation: true,
      useExtendedSearch: true,
    });
  }, [index]);

  useEffect(() => {
    if (debounceRef.current !== null) {
      window.clearTimeout(debounceRef.current);
    }
    debounceRef.current = window.setTimeout(() => {
      if (!fuse) {
        onResults([]);
        return;
      }
      if (value.trim().length === 0) {
        onResults([]);
        return;
      }
      const matches = fuse.search(value, { limit: maxResults }).map((m) => m.item);
      onResults(matches);
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current !== null) {
        window.clearTimeout(debounceRef.current);
      }
    };
  }, [value, fuse, maxResults, onResults]);

  return (
    <div
      className={
        'relative flex items-center border border-dash-border rounded-md bg-dash-bg ' +
        (className ?? '')
      }
    >
      <span className="pl-3 text-dash-muted" aria-hidden="true">
        <IconSearch size={14} />
      </span>
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setHasFocused(true)}
        placeholder={placeholder ?? 'Search help...'}
        autoFocus={autoFocus}
        aria-label="Search help"
        className="flex-1 bg-transparent px-2 py-2 text-sm text-dash-primary placeholder:text-dash-muted focus:outline-none"
      />
    </div>
  );
}
